import React from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { Appbar, Avatar, Divider, List, Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";
import { useTranslation } from "react-i18next";
import { alert as showAlert } from "../../utils/alert";
import FileDetailsEmptyState from "./components/FileDetailsEmptyState";
import RenameFileDialog from "./components/RenameFileDialog";
import { useFileManagement } from "./hooks/useFileManagement";
import { downloadDriveFile } from "./utils/downloadDriveFile";
import {
  getModifiedDisplayValue,
  getTimestampDisplayValue,
} from "./utils/formatTime";
import { getFileIcon } from "./utils/getFileIcon";
import { getFileKindLabel } from "./utils/getFileKindLabel";

interface FileDetailsScreenProps {
  navigation: any;
  route: any;
}

export default function FileDetailsScreen({
  navigation,
  route,
}: FileDetailsScreenProps) {
  const { t } = useTranslation();
  const fileManagement = useFileManagement({
    initialFile: route.params?.file,
    onFileUpdate: (updatedFile) =>
      navigation.setParams?.({ file: updatedFile }),
    onFileDelete: () => navigation.goBack(),
  });
  const file = fileManagement.file;

  const handleDownload = async () => {
    if (!file) return;

    try {
      await downloadDriveFile(file);
    } catch (error) {
      console.error("Failed to download file:", error);
      showAlert(t("Error"), t("Failed to download file. Please try again."), [
        { text: t("OK") },
      ]);
    }
  };

  const handleShare = () => {
    console.log("Share file");
  };

  const handleRename = () => {
    fileManagement.openRenameDialog();
  };

  const handleDelete = () => {
    fileManagement.openDeleteDialog();
  };

  if (!file) {
    return <FileDetailsEmptyState onBack={() => navigation.goBack()} />;
  }

  const fileKind = getFileKindLabel(file.type);
  const modifiedLabel = getModifiedDisplayValue(file);
  const updatedValue = getTimestampDisplayValue(
    file.updatedAt || file.createdAt,
  );
  return (
    <View style={styles.container}>
      <Appbar.Header>
        <Appbar.BackAction onPress={() => navigation.goBack()} />
        <Appbar.Content title={t("File Details")} />
      </Appbar.Header>

      <ScrollView style={styles.content}>
        <View style={styles.filePreview}>
          <Avatar.Icon
            size={128}
            icon={getFileIcon(file.type)}
            style={styles.fileIcon}
          />
          <Text variant="headlineSmall" style={styles.fileName}>
            {file.name}
          </Text>
          <View style={styles.fileMetaRow}>
            <Text variant="bodyMedium" style={styles.fileMeta}>
              {file.size}
            </Text>
            <Text style={styles.separator}>•</Text>
            <Text variant="bodyMedium" style={styles.fileMeta}>
              {modifiedLabel}
            </Text>
          </View>
        </View>

        <View style={styles.section}>
          <Text variant="labelLarge" style={styles.sectionTitle}>
            {t("Actions")}
          </Text>
        </View>

        <View style={styles.actionsList}>
          <List.Item
            title={t("Download")}
            description={t("Save to device storage")}
            titleStyle={styles.actionTitle}
            left={(props) => (
              <Avatar.Icon
                {...props}
                size={44}
                icon="download"
                style={styles.actionIconPrimary}
              />
            )}
            right={(props) => <List.Icon {...props} icon="chevron-right" />}
            onPress={handleDownload}
            style={styles.actionItem}
          />
          <Divider />

          <List.Item
            title={t("Share")}
            description={t("Send to others or apps")}
            titleStyle={styles.actionTitle}
            left={(props) => (
              <Avatar.Icon
                {...props}
                size={44}
                icon="share"
                style={styles.actionIconPrimary}
              />
            )}
            right={(props) => <List.Icon {...props} icon="chevron-right" />}
            onPress={handleShare}
            style={styles.actionItem}
          />
          <Divider />

          <List.Item
            title={t("Rename")}
            titleStyle={styles.actionTitle}
            left={(props) => (
              <Avatar.Icon
                {...props}
                size={44}
                icon="pencil"
                style={styles.actionIconSecondary}
              />
            )}
            right={(props) => <List.Icon {...props} icon="chevron-right" />}
            onPress={handleRename}
            style={styles.actionItem}
          />
          <Divider />

          <List.Item
            title={t("Delete")}
            titleStyle={styles.actionTitleDanger}
            left={(props) => (
              <Avatar.Icon
                {...props}
                size={44}
                icon="delete"
                style={styles.actionIconDanger}
              />
            )}
            right={(props) => <List.Icon {...props} icon="chevron-right" />}
            onPress={handleDelete}
            style={styles.actionItem}
          />
        </View>

        <View style={styles.section}>
          <Text variant="labelLarge" style={styles.sectionTitle}>
            {t("File metadata")}
          </Text>
        </View>

        <View style={styles.metadataCard}>
          <View style={styles.metadataRow}>
            <Text variant="bodyMedium" style={styles.metadataLabel}>
              {t("Kind")}
            </Text>
            <Text variant="bodyMedium" style={styles.metadataValue}>
              {t(fileKind)}
            </Text>
          </View>
          <Divider />

          <View style={styles.metadataRow}>
            <Text variant="bodyMedium" style={styles.metadataLabel}>
              {t("Updated")}
            </Text>
            <Text variant="bodyMedium" style={styles.metadataValue}>
              {updatedValue}
            </Text>
          </View>
        </View>

        <SafeAreaView edges={["bottom"]} />
      </ScrollView>

      <RenameFileDialog
        visible={fileManagement.renameDialogVisible}
        fileName={fileManagement.fileName}
        error={fileManagement.renameError}
        onChangeName={fileManagement.handleFileNameChange}
        onConfirm={fileManagement.saveRenamedFile}
        onCancel={fileManagement.closeRenameDialog}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
  filePreview: {
    alignItems: "center",
    paddingVertical: 32,
    paddingHorizontal: 16,
  },
  fileIcon: {
    backgroundColor: "rgba(32, 148, 243, 0.1)",
    marginBottom: 16,
  },
  fileName: {
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 8,
  },
  fileMetaRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  fileMeta: {
    opacity: 0.6,
  },
  separator: {
    opacity: 0.3,
  },
  section: {
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  sectionTitle: {
    fontWeight: "600",
    opacity: 0.6,
  },
  actionsList: {
    marginBottom: 24,
  },
  actionItem: {
    paddingVertical: 8,
    paddingLeft: 8,
  },
  actionTitle: {
    fontWeight: "600",
  },
  actionTitleDanger: {
    fontWeight: "600",
    color: "#ef4444",
  },
  actionIconPrimary: {
    backgroundColor: "rgba(32, 148, 243, 0.1)",
  },
  actionIconSecondary: {
    backgroundColor: "rgba(100, 116, 139, 0.1)",
  },
  actionIconDanger: {
    backgroundColor: "rgba(239, 68, 68, 0.1)",
  },
  metadataCard: {
    marginHorizontal: 16,
    marginBottom: 24,
  },
  metadataRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 16,
    gap: 16,
  },
  metadataLabel: {
    opacity: 0.6,
  },
  metadataValue: {
    flex: 1,
    fontWeight: "500",
    textAlign: "right",
  },
});
